import React, { useState, useEffect, useMemo } from 'react';
import type { Task, GanttData } from '../types';
import { Modal } from './Modal';
import { diffDays, addDays } from '../dateUtils';

type Props = {
  task: Task | null;       // null = dialog closed
  data: GanttData;         // Needed to look up dependency names
  onSave: (id: string, patch: Partial<Omit<Task, 'id'>>) => void;
  onDelete: (id: string) => void;
  onRemoveDependency: (depId: string) => void; // New callback
  onClose: () => void;
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '6px 8px',
  border: '1px solid #d0d0d0',
  borderRadius: 4,
  fontSize: 13,
  outline: 'none',
};

export const TaskEditDialog: React.FC<Props> = ({ 
  task, 
  data, 
  onSave, 
  onDelete, 
  onRemoveDependency, 
  onClose 
}) => {
  const [title, setTitle] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [duration, setDuration] = useState(1);
  const [isResolved, setIsResolved] = useState(false);

  // Initialize fields when task opens
  useEffect(() => {
    if (!task) return;
    setTitle(task.title);
    setStartDate(task.startDate);
    setEndDate(task.endDate);
    setDuration(Math.max(1, diffDays(task.startDate, task.endDate)));
    setIsResolved(task.isResolved ?? false);
  }, [task]);

  // Find dependencies related to this task
  const { incoming, outgoing } = useMemo(() => {
    if (!task) return { incoming: [], outgoing: [] };
    
    const taskMap = new Map(data.tasks.map(t => [t.id, t]));

    const incomingDeps = data.dependencies
      .filter(d => d.toTaskId === task.id)
      .map(d => ({ 
        ...d, 
        otherTaskTitle: taskMap.get(d.fromTaskId)?.title || 'Unknown' 
      }));

    const outgoingDeps = data.dependencies
      .filter(d => d.fromTaskId === task.id)
      .map(d => ({ 
        ...d, 
        otherTaskTitle: taskMap.get(d.toTaskId)?.title || 'Unknown' 
      }));

    return { incoming: incomingDeps, outgoing: outgoingDeps };
  }, [task, data.dependencies, data.tasks]);


  const handleStartChange = (val: string) => {
    setStartDate(val);
    const newEnd = addDays(val, duration);
    setEndDate(newEnd);
  };

  const handleEndChange = (val: string) => {
    const d = diffDays(startDate, val);
    if (d < 1) {
      const snapped = addDays(startDate, 1);
      setEndDate(snapped);
      setDuration(1);
    } else {
      setEndDate(val);
      setDuration(d);
    }
  };

  const handleDurationChange = (val: number) => {
    const d = Math.max(1, val);
    setDuration(d);
    setEndDate(addDays(startDate, d));
  };

  const handleSave = () => {
    if (!task) return;
    onSave(task.id, { 
      title: title.trim() || 'Untitled', 
      startDate, 
      endDate,
      isResolved 
    });
    onClose();
  };

  const handleDelete = () => {
    if (!task) return;
    onDelete(task.id);
    onClose();
  };

  return (
    <Modal open={!!task} onClose={onClose} title="Edit Task" width={420}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

        {/* Title */}
        <label>
          <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Title</div>
          <input
            value={title}
            onChange={e => setTitle(e.target.value)}
            style={inputStyle}
            onKeyDown={e => { if (e.key === 'Enter') handleSave(); }}
            autoFocus
          />
        </label>

        {/* Dates */}
        <div style={{ display: 'flex', gap: 8 }}>
          <label style={{ flex: 1 }}>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Start Date</div>
            <input
              type="date"
              value={startDate}
              onChange={e => handleStartChange(e.target.value)}
              style={inputStyle}
            />
          </label>
          <label style={{ flex: 1 }}>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>End Date</div>
            <input
              type="date"
              value={endDate}
              onChange={e => handleEndChange(e.target.value)}
              style={inputStyle}
            />
          </label>
        </div>

        {/* Duration & Status */}
        <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
          <label style={{ width: 120 }}>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Duration (days)</div>
            <input
              type="number"
              min={1}
              value={duration}
              onChange={e => handleDurationChange(Number(e.target.value))}
              style={{ ...inputStyle, width: 100 }}
            />
          </label>

          <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 18, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={isResolved}
              onChange={e => setIsResolved(e.target.checked)}
              style={{ width: 16, height: 16, cursor: 'pointer' }}
            />
            <span style={{ fontSize: 13, color: '#333' }}>Task is resolved</span>
          </label>
        </div>

        {/* Dependencies List */}
        {(incoming.length > 0 || outgoing.length > 0) && (
          <div style={{ marginTop: 8, borderTop: '1px solid #eee', paddingTop: 8 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#444', marginBottom: 8 }}>Dependencies</div>
            
            <div style={{ maxHeight: 120, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
              {incoming.map(dep => (
                <DependencyItem 
                  key={dep.id} 
                  label={`← From: ${dep.otherTaskTitle} (${dep.type})`} 
                  onRemove={() => onRemoveDependency(dep.id)} 
                />
              ))}
              {outgoing.map(dep => (
                <DependencyItem 
                  key={dep.id} 
                  label={`→ To: ${dep.otherTaskTitle} (${dep.type})`} 
                  onRemove={() => onRemoveDependency(dep.id)} 
                />
              ))}
            </div>
          </div>
        )}

        {/* Buttons */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
          <button onClick={handleDelete} style={deleteBtnStyle}>
            Delete task
          </button>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onClose} style={cancelBtnStyle}>Cancel</button>
            <button onClick={handleSave} style={saveBtnStyle}>Save</button>
          </div>
        </div>

      </div>
    </Modal>
  );
};

// --- Helper Component for Dependency List Item ---

const DependencyItem: React.FC<{ label: string; onRemove: () => void }> = ({ label, onRemove }) => (
  <div style={{ 
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'space-between', 
    background: '#f9f9f9', 
    padding: '4px 8px', 
    borderRadius: 4,
    fontSize: 12 
  }}>
    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
    <button 
      onClick={onRemove}
      style={{ 
        border: 'none', 
        background: 'transparent', 
        color: '#999', 
        cursor: 'pointer', 
        fontSize: 14, 
        fontWeight: 'bold',
        marginLeft: 8,
        padding: '0 4px'
      }}
      title="Remove dependency"
    >
      &times;
    </button>
  </div>
);

// --- Styles ---

const deleteBtnStyle: React.CSSProperties = {
  padding: '6px 14px',
  background: '#fef2f2',
  color: '#dc2626',
  border: '1px solid #fca5a5',
  borderRadius: 4,
  cursor: 'pointer',
  fontSize: 13,
};

const cancelBtnStyle: React.CSSProperties = {
  padding: '6px 14px',
  background: '#f5f5f5',
  color: '#333',
  border: '1px solid #d0d0d0',
  borderRadius: 4,
  cursor: 'pointer',
  fontSize: 13,
};

const saveBtnStyle: React.CSSProperties = {
  padding: '6px 14px',
  background: '#3b82f6',
  color: '#fff',
  border: 'none',
  borderRadius: 4,
  cursor: 'pointer',
  fontSize: 13,
};
